import { Validators } from '@angular/forms';
import { AppConstants } from '../../app.constants';
import { AtLeastOneValidator } from './rl-custom-at-least-one.validator';
import { ExactlyOneValidator } from './rl-custom-exaclty-one.validator';
import { CustomPatternValidator } from './rl-custom-pattern.validator';
import { CustomRequiredValidator } from './rl-custom-required.validator';
import { CustomUniqueFormArrayValidator } from './rl-custom-unique-form-array.validator';
import { RLMustMatchValidator } from './rl-must-match.validator';
import { RLValidatorRegExConstants } from './rl-validator-regex.constant';

export class RLValidatorConstants {
    public static readonly KEYS = {
        REQUIRED: 'required',
        MIN_LENGTH: 'minlength',
        MAX_LENGTH: 'maxlength',
        MIN: 'min',
        MAX: 'max',
        PASSWORD: 'password',
        EMAIL: 'email',
        PATTERN: 'pattern',
        NOT_ENOUGH_ITEMS: 'notenoughitems',
        EVEN_NUMBER: 'evennumber',
        URL: 'url',
        ALPHANUMERIC: 'alphanumeric',
        NUMBER: 'number',
        EXACTLY_ONE: 'exactlyone',
        UNIQUE_FORM_ARRAY: 'uniqueformarray'
    };

    // Message keys are concatenated without underscore because reactive forms needs valid object properties
    public static readonly MESSAGES = {
        REQUIRED: 'Required field',
        MINLENGTH: 'Value must be at least 4 characters long',
        MAXLENGTH: 'Value cannot be more than 24 characters long',
        MIN: 'Amount should at least be 0',
        MAX: 'Amount is too large, please lower the value', // Number.MAX_VALUE
        PASSWORD: 'Password requires at least 10 characters',
        EMAIL: 'Not a valid email address',
        VALIDDATERANGE: 'Please enter a valid date range',
        NOTEQUALS: 'Passwords do not match',
        EVEN_NUMBER: 'The number you have entered should be an even number (e.g. 2, 4, 6, 20, 100)',
        EAN: 'Please enter a valid ean number, more information about EAN/GTIN ' + '<a href="http://www.gtin.info" target="_blank">here</a>',
        MIN_INDESIGN_BOUNDARIES: `Value should at least be ${AppConstants.INDESIGN_DOCUMENT_BOUNDARIES.MINIMUM_HEIGHT_AND_WIDTH}`,
        URL: 'Please enter a valid url',
        ALPHANUMERIC: "Value can only contain letters, numbers, '-', '_' and '?'",
        NUMBER: 'Value should be a valid number',
        GREATER_THAN_ZERO: 'Value should be bigger than 0',
        EXACTLY_ONE: 'There should be exactly one value',
        UNIQUE_FORM_ARRAY: 'Value cannot be used multiple times',
        DATA_FIELD_NAME: "Value can only contain letters, numbers, spaces, '-' and '_'",
        UNIQUE: 'Value should be unique',
        LAYOUT_REQUIRED: 'Layout is required when using PDF Export settings',
        NUMBERS_WITH_OPTIONAL_PERCENTAGE: 'Value can only contain numbers and optionally a % at the end',
        ALPHANUMERIC_STRICT: 'Identifier may only contain alphanumeric characters',
        DUPLICATE: 'Duplicate field not allowed'
    };

    public static readonly MESSAGE_SETS = {
        TAGS: {
            notenoughitems: 'Please add more tags',
            required: RLValidatorConstants.MESSAGES.REQUIRED
        },
        EXACTLY_ONE: {
            exactlyone: RLValidatorConstants.MESSAGES.EXACTLY_ONE
        },
        REQUIRED: {
            required: RLValidatorConstants.MESSAGES.REQUIRED
        },
        EMAIL: {
            required: RLValidatorConstants.MESSAGES.REQUIRED,
            pattern: RLValidatorConstants.MESSAGES.EMAIL
        },
        EAN: {
            pattern: RLValidatorConstants.MESSAGES.EAN
        },
        DATERANGE: {
            required: RLValidatorConstants.MESSAGES.VALIDDATERANGE
        },
        POSITIVE_NUMBER: {
            min: RLValidatorConstants.MESSAGES.MIN,
            max: RLValidatorConstants.MESSAGES.MAX
        },
        POSITIVE_NUMBER_REQUIRED: {
            required: RLValidatorConstants.MESSAGES.REQUIRED,
            min: RLValidatorConstants.MESSAGES.MIN,
            max: RLValidatorConstants.MESSAGES.MAX
        },
        INDESIGN_DOCUMENT_SIZE_BOUNDARIES: {
            required: RLValidatorConstants.MESSAGES.REQUIRED,
            min: RLValidatorConstants.MESSAGES.MIN_INDESIGN_BOUNDARIES
        },
        URL: {
            pattern: RLValidatorConstants.MESSAGES.URL
        },
        ALPHANUMERIC: {
            required: RLValidatorConstants.MESSAGES.REQUIRED,
            pattern: RLValidatorConstants.MESSAGES.ALPHANUMERIC
        },
        NUMBER: {
            pattern: RLValidatorConstants.MESSAGES.NUMBER
        },
        GREATER_THAN_ZERO: {
            required: RLValidatorConstants.MESSAGES.REQUIRED,
            min: RLValidatorConstants.MESSAGES.GREATER_THAN_ZERO
        },
        UNIQUE_FORM_ARRAY: {
            uniqueformarray: RLValidatorConstants.MESSAGES.UNIQUE_FORM_ARRAY
        }
    };

    public static readonly VALIDATOR_SETS = {
        PASSWORD: [CustomRequiredValidator, Validators.required, Validators.minLength(10)],
        EXACTLY_ONE: [ExactlyOneValidator],
        TAGS: [AtLeastOneValidator],
        NAME: [CustomRequiredValidator, Validators.required],
        EMAIL: [CustomRequiredValidator, Validators.required, CustomPatternValidator(RLValidatorRegExConstants.EMAIL, RLValidatorConstants.MESSAGES.EMAIL)],
        EAN: [CustomPatternValidator(RLValidatorRegExConstants.EAN, RLValidatorConstants.MESSAGES.EAN)],
        PATTERN: (pattern: string | RegExp, message: string) => CustomPatternValidator(pattern, message),
        REQUIRED: [CustomRequiredValidator, Validators.required],
        REQUIRED_NUMBER: [Validators.required],
        GREATER_THAN_ZERO: [CustomRequiredValidator, Validators.required, Validators.min(1)],
        EVEN_NUMBER: [
            CustomRequiredValidator,
            Validators.required,
            CustomPatternValidator(RLValidatorRegExConstants.EVEN_NUMBER, RLValidatorConstants.MESSAGES.EVEN_NUMBER),
            Validators.min(0),
            Validators.max(1000)
        ],
        POSITIVE_NUMBER: [Validators.min(0), Validators.max(Number.MAX_VALUE)],
        POSITIVE_NUMBER_REQUIRED: [Validators.required, Validators.min(0), Validators.max(Number.MAX_VALUE)],
        INDESIGN_DOCUMENT_SIZE_BOUNDARIES: [
            CustomRequiredValidator,
            Validators.required,
            Validators.min(AppConstants.INDESIGN_DOCUMENT_BOUNDARIES.MINIMUM_HEIGHT_AND_WIDTH)
        ],
        URL: [CustomPatternValidator(RLValidatorRegExConstants.URL, RLValidatorConstants.MESSAGES.URL)],
        ALPHANUMERIC: [
            CustomRequiredValidator,
            Validators.required,
            CustomPatternValidator(RLValidatorRegExConstants.ALPHANUMERIC, RLValidatorConstants.MESSAGES.ALPHANUMERIC)
        ],
        NUMBER: [CustomPatternValidator(RLValidatorRegExConstants.NUMBER, RLValidatorConstants.MESSAGES.NUMBER)],
        UNIQUE_FORM_ARRAY: [CustomUniqueFormArrayValidator],
        DATA_FIELD_NAME: [Validators.required, CustomPatternValidator(RLValidatorRegExConstants.DATA_FIELD_NAME, RLValidatorConstants.MESSAGES.DATA_FIELD_NAME)]
    };

    public static readonly FORM_GROUP_VALIDATOR = {
        MUST_MATCH: RLMustMatchValidator
    };

    public static readonly FORM_FIELD_ERROR_MESSAGES_VALUE = {
        required: RLValidatorConstants.MESSAGES.REQUIRED,
        email: RLValidatorConstants.MESSAGES.EMAIL,
        pattern: 'Invalid pattern',
        minControlsLength: ({ error }: any) => `Must have at least ${error?.requiredLength} value${error?.requiredLength > 0 ? 's' : ''}`,
        minlength: ({ error, label }: any) => `${label ?? 'Value'} must be at least ${error?.requiredLength} characters`,
        maxlength: ({ error, label }: any) => `${label ?? 'Value'} cannot be more than ${error?.requiredLength} characters`,
        min: ({ error }: any) => `Must be at least ${error?.min}`,
        max: ({ error }: any) => `Cannot exceed ${error?.max}`,
        default: ({ key, label }: any) => `${label ?? 'Field'} is invalid (${key}).`,
        // Custom Relayter errors
        mustMatch: RLValidatorConstants.MESSAGES.NOTEQUALS,
        duplicateValues: RLValidatorConstants.MESSAGES.UNIQUE,
        layoutRequired: RLValidatorConstants.MESSAGES.LAYOUT_REQUIRED,
        duplicate: RLValidatorConstants.MESSAGES.DUPLICATE
    };
}
